import React from 'react';
import { useMutation } from '@apollo/client/react';
import { Stack, Button } from '@mui/material';

import { useNavigate } from 'react-router';
import {
  InvoiceFragment,
  CreateInvoiceDocument,
  CreateInvoiceMutation,
  CreateInvoiceMutationVariables,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';
import { appPaths } from '../../../app-paths';
import AddIcon from '@mui/icons-material/Add';
import { InvoiceDataGrid } from '../../../components/invoice-datagrid';

interface InvoiceListProps {
  contractId: string;
  invoices: InvoiceFragment[];
  onRefresh?: () => void;
  disabled?: boolean;
  showRecurringButton?: boolean;
  pageSize?: number;
}

export function InvoiceList({ contractId, invoices, onRefresh, disabled, pageSize }: InvoiceListProps) {
  const app = useApp();
  const { show } = useAlert();
  const navigate = useNavigate();

  const [createInvoice, { loading: createLoading }] = useMutation<
    CreateInvoiceMutation,
    CreateInvoiceMutationVariables
  >(CreateInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa vytvoriť faktúru.', e.message);
    },
  });

  const handleCreateInvoice = async () => {
    try {
      const result = await createInvoice({
        variables: {
          input: {
            contractId,
            description: ' ',
            totalAmount: 0,
          },
        },
      });
      if (result.data?.createInvoice.invoice?.id) {
        if (onRefresh) {
          onRefresh();
        }
        navigate(appPaths.invoice.navigate({ invoiceId: result.data.createInvoice.invoice.id }));
      }
    } catch (e) {
      show.error('Nepodarilo sa vytvoriť faktúru.', (e as Error).message);
    }
  };

  return (
    <Stack direction="column" flexDirection="column" width="100%" sx={{ height: 400 }} spacing={2}>
      <Stack direction="row" justifyContent="flex-start">
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleCreateInvoice}
          disabled={disabled || createLoading}
        >
          Vytvor faktúru
        </Button>
      </Stack>

      <InvoiceDataGrid
        invoices={invoices}
        formatNumber={app.formatNumber}
        formatDate={app.formatDate}
        onRowClick={(invoiceId) => navigate(appPaths.invoice.navigate({ invoiceId }))}
        showContractColumn={false}
        pageSize={pageSize}
      />
    </Stack>
  );
}
